/**
 * Sprint 5-a-hotfix-1 · StepReviewBanner · 每步審核 **inline banner**（非 modal）。
 *
 * 原 StepReviewDialog 是 MUI Dialog（modal overlay·擋住左側 Excel iframe）→ hotfix 改為
 * chat panel 內的 inline banner，製作區(iframe)完全 unblocked，使用者可 click cell / scroll / zoom
 * 後再決定 [繼續 / 修改 / 停止]。每個按鈕仍明確標「計費」（費用透明度·應答就計費）。
 */
import React, { useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface Props {
  stepNo: number;
  isLast: boolean;
  onContinue: () => void;
  onReplan: (feedback: string) => void;
  onAbort: () => void;
}

const cap = (main: string, sub: string) => (
  <Stack sx={{ lineHeight: 1.15, alignItems: 'center' }}>
    <span>{main}</span>
    <Typography variant="caption" component="span" sx={{ opacity: 0.8 }}>{sub}</Typography>
  </Stack>
);

const StepReviewBanner: React.FC<Props> = ({ stepNo, isLast, onContinue, onReplan, onAbort }) => {
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`agentic.${k}`, d);
  const [replanMode, setReplanMode] = useState(false);
  const [feedback, setFeedback] = useState('');
  const reset = () => { setReplanMode(false); setFeedback(''); };

  return (
    <Box sx={{
      mt: 1, p: 1.5, borderRadius: 1,
      border: '1px solid', borderColor: 'primary.main', bgcolor: 'action.hover',
    }}>
      <Typography variant="subtitle2" gutterBottom>
        ✅ {tt('reviewTitle', '這一步完成了')}（{tt('step', '步驟')} {stepNo}）
      </Typography>
      {!replanMode ? (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {isLast ? tt('reviewLastHint', '這是最後一步，滿意就繼續完成。')
              : tt('reviewHint', '檢視左側 sheet 的結果，再決定下一步：')}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
            <Button variant="contained" size="small" onClick={onContinue}>
              {cap(isLast ? tt('finishAll', '完成') : tt('continueNext', '繼續下一步'),
                isLast ? tt('finishAllSub', '結束製作') : `${tt('startNext', '啟動下一步')} · ${tt('newCharge', '新計費')}`)}
            </Button>
            <Button variant="outlined" size="small" onClick={() => setReplanMode(true)}>
              {cap(tt('replan', '修改這步'), `${tt('rerunStep', '重跑本步')} · ${tt('extraCharge', '額外計費')}`)}
            </Button>
            <Button variant="outlined" size="small" color="warning" onClick={onAbort}>
              {cap(tt('abortAll', '全部停止'), tt('abortSub', '結束 · 累計已計'))}
            </Button>
          </Stack>
        </>
      ) : (
        <Stack spacing={1}>
          <TextField autoFocus fullWidth multiline minRows={2} size="small"
            label={tt('replanLabel', '想怎麼修改這一步？')}
            value={feedback} onChange={(e) => setFeedback(e.target.value)}
            placeholder={tt('replanPlaceholder', '例如：改用表格呈現、語氣更正式…')} />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" disabled={!feedback.trim()}
              onClick={() => { onReplan(feedback.trim()); reset(); }}>
              {cap(tt('applyReplan', '重新產生這步'), tt('extraCharge', '額外計費'))}
            </Button>
            <Button variant="text" size="small" onClick={reset}>{tt('back', '返回')}</Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default StepReviewBanner;
